import { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

export const useUserAccess = () => {
  const { currentUser } = useAuth();
  const [userAccess, setUserAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const normalizedEmail = currentUser?.email?.trim().toLowerCase();
    const originalEmail = currentUser?.email;

    if (!normalizedEmail) {
      setUserAccess(null);
      setLoading(false);
      return;
    }

    const primaryRef = doc(db, 'userAccess', normalizedEmail);
    const legacyRef = originalEmail && originalEmail !== normalizedEmail
      ? doc(db, 'userAccess', originalEmail)
      : null;

    const syncAccess = async (snapshot) => {
      if (snapshot.exists()) {
        setUserAccess(snapshot.data());
        setLoading(false);
        return;
      }

      if (legacyRef) {
        try {
          const legacySnapshot = await getDoc(legacyRef);
          if (legacySnapshot.exists()) {
            setUserAccess(legacySnapshot.data());
            setLoading(false);
            return;
          }
        } catch (error) {
          console.warn('Legacy access lookup failed:', error);
        }
      }

      setUserAccess({
        hasAccess: false,
        storyTokens: 0,
        tokensUsed: 0
      });
      setLoading(false);
    };

    // Listen to the normalized access doc first; fall back to legacy casing for older records.
    const unsubscribe = onSnapshot(primaryRef, syncAccess, (error) => {
      console.error('Error fetching user access:', error);
      setUserAccess({
        hasAccess: false,
        storyTokens: 0,
        tokensUsed: 0
      });
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser?.email]);

  const canCreateStory = () => {
    if (!userAccess) return false;
    return userAccess.storyTokens > userAccess.tokensUsed;
  };

  const remainingTokens = () => {
    if (!userAccess) return 0;
    return Math.max(0, userAccess.storyTokens - userAccess.tokensUsed);
  };

  return {
    userAccess,
    loading,
    canCreateStory: canCreateStory(),
    remainingTokens: remainingTokens(),
    hasAccess: userAccess?.hasAccess || false
  };
};
